import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/Layout/DashboardLayout';
import ArrowLeftIcon from '../../components/icons/ArrowLeftIcon';
import { useAuth } from '../../contexts/AuthContext'; // Import useAuth
import FormSpinnerOverlay from '../../components/ui/FormSpinnerOverlay'; // Import FormSpinnerOverlay
import AlertMessage from '../../components/ui/AlertMessage'; // For displaying success/error messages

const ServiceFormSupplierScreen = () => {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const { serviceId } = useParams(); // Get serviceId from URL params
    const isEditMode = Boolean(serviceId);
    const { token, userId } = useAuth(); // Get token and userId from AuthContext
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        price: '',
        capacity: '',
        duration: '',
        startTime: '',
        endTime: '',
        weeklyAvailability: [],
        additionalConditions: '',
        accessibilityInfo: '',
        serviceOrigin: '',
        serviceDestination: '',
    });
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedAvailability, setSelectedAvailability] = useState('');

    const [categories, setCategories] = useState([]);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [errorCategories, setErrorCategories] = useState(null);

    const [availabilityOptions, setAvailabilityOptions] = useState([]);
    const [loadingAvailability, setLoadingAvailability] = useState(true);
    const [errorAvailability, setErrorAvailability] = useState(null);

    const [transportOptions, setTransportOptions] = useState([]);
    const [loadingTransportOptions, setLoadingTransportOptions] = useState(false); // Initially false, load when category '3' is selected
    const [errorTransportOptions, setErrorTransportOptions] = useState(null);
    const [selectedTransportOptions, setSelectedTransportOptions] = useState([]);

    const [foodOptions, setFoodOptions] = useState([]);
    const [loadingFoodOptions, setLoadingFoodOptions] = useState(false); // Initially false, load when category '2' is selected
    const [errorFoodOptions, setErrorFoodOptions] = useState(null);
    const [selectedFoodOptions, setSelectedFoodOptions] = useState([]);

    const [hostingFeatures, setHostingFeatures] = useState([]);
    const [loadingHostingFeatures, setLoadingHostingFeatures] = useState(false); // Initially false, load when category '1' is selected
    const [errorHostingFeatures, setErrorHostingFeatures] = useState(null);
    const [selectedHostingFeatures, setSelectedHostingFeatures] = useState([]);

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoadingServiceData, setIsLoadingServiceData] = useState(false);
    const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });
    const alertRef = useRef(null); // Ref for the alert message

    // New state: images grouped by category and input section
    // Structure: { [category]: { [inputSection]: [ { id, image_url, ... } ] } }
    const [serviceImages, setServiceImages] = useState({});
    // Track uploading state for each image section
    const [imageUploading, setImageUploading] = useState({});

    // Modal state for lightbox (must be at the top level, before any logic or functions)
    const [modalOpen, setModalOpen] = useState(false);
    const [modalImg, setModalImg] = useState(null);

    // Handler to open modal
    const handleOpenImageModal = (img) => {
        setModalImg(img);
        setModalOpen(true);
    };

    // Handler to close modal
    const handleCloseImageModal = () => {
        setModalOpen(false);
        setModalImg(null);
    };

    // Handler to delete image
    const handleDeleteImage = async (img) => {
        if (!img.id) return;
        if (!window.confirm('¿Está seguro de que desea eliminar esta imagen?')) return;
        try {
            // Use the backend delete endpoint
            const response = await fetch(`${API_BASE_URL}/api/service-images/${img.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                throw new Error('No se pudo eliminar la imagen');
            }
            // Refresh images after delete
            await fetchServiceImages();
            handleCloseImageModal();
        } catch (error) {
            alert(error.message || 'Error al eliminar la imagen');
        }
    };

    const handleCategoryChange = (event) => {
        setSelectedCategory(event.target.value);
        // Reset transport options if category changes from '3'
        if (event.target.value !== '3') {
            setSelectedTransportOptions([]);
        }
        // Reset food options if category changes from '2'
        if (event.target.value !== '2') {
            setSelectedFoodOptions([]);
            // Optionally, you might want to clear foodOptions as well if you don't want to cache them
            // setFoodOptions([]);
        }
        // Reset hosting features if category changes from '1'
        if (event.target.value !== '1') {
            setSelectedHostingFeatures([]);
        }
    };
    const handleAvailabilityChange = (event) => {
        setSelectedAvailability(event.target.value);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleWeeklyAvailabilityChange = (e) => {
        const { value, checked } = e.target;
        setFormData(prev => {
            const currentDays = prev.weeklyAvailability;
            if (checked) {
                return { ...prev, weeklyAvailability: [...currentDays, value] };
            } else {
                return { ...prev, weeklyAvailability: currentDays.filter(day => day !== value) };
            }
        });
    };

    useEffect(() => {
        const fetchServiceDetails = async () => {
            if (isEditMode && serviceId && token) {
                setIsLoadingServiceData(true);
                setSubmitMessage({ type: '', text: '' }); // Clear previous messages
                try {
                    const response = await fetch(`${API_BASE_URL}/api/services/${serviceId}`, {
                        headers: { 'Authorization': `Bearer ${token}` },
                    });
                    if (!response.ok) {
                        const errorData = await response.json().catch(() => ({ message: `Error fetching service details: ${response.status}` }));
                        throw new Error(errorData.message || `Error fetching service details: ${response.status}`);
                    }
                    const data = await response.json();

                    setFormData({
                        name: data.name || '',
                        description: data.description || '',
                        price: data.price !== null && data.price !== undefined ? String(data.price) : '',
                        capacity: data.capacity !== null && data.capacity !== undefined ? String(data.capacity) : '',
                        duration: data.duration || '',
                        startTime: data.start_time || '',
                        endTime: data.end_time || '',
                        weeklyAvailability: data.weekly_availability ? data.weekly_availability.split(',') : [],
                        additionalConditions: data.additional_conditions || '',
                        accessibilityInfo: data.accessibility_info || '',
                        serviceOrigin: data.service_origin || '',
                        serviceDestination: data.service_destination || '',
                    });
                    setSelectedCategory(data.category_id ? String(data.category_id) : '');
                    setSelectedAvailability(data.availability_status_id ? String(data.availability_status_id) : '');

                    // Correctly map selected features/options from arrays of objects to arrays of IDs (as strings)
                    setSelectedHostingFeatures(
                        data.selected_hosting_features
                            ? data.selected_hosting_features.map(feature => String(feature.id))
                            : []
                    );
                    setSelectedFoodOptions(
                        data.selected_food_options
                            ? data.selected_food_options.map(option => String(option.id))
                            : []
                    );
                    setSelectedTransportOptions(
                        data.selected_transport_options
                            ? data.selected_transport_options.map(option => String(option.id))
                            : []
                    );

                } catch (error) {
                    console.error("Error fetching service details:", error);
                    setSubmitMessage({ type: 'danger', text: `Error al cargar los detalles del servicio: ${error.message}` });
                } finally {
                    setIsLoadingServiceData(false);
                }
            }
        };
        fetchServiceDetails();
    }, [isEditMode, serviceId, API_BASE_URL, token]);

    // Fetch existing images for the service and organize by category and input section
    // Define fetchServiceImages at the component level so it can be used in the upload handler
    const fetchServiceImages = async () => {
        if (isEditMode && serviceId && token) {
            try {
                const response = await fetch(`${API_BASE_URL}/api/service-images/service/${serviceId}`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                });
                if (!response.ok) {
                    throw new Error(`Failed to fetch service images: ${response.status}`);
                }
                let images = await response.json();
                // Defensive: filter out any images with deleted_at set (should not be needed if backend is correct)
                images = images.filter(img => !img.deleted_at);
                // Organize images by category and inputType
                const groupedImages = {};
                images.forEach(img => {
                    const category = img.category || 'uncategorized';
                    const inputType = img.input_type || 'default';
                    if (!groupedImages[category]) groupedImages[category] = {};
                    if (!groupedImages[category][inputType]) groupedImages[category][inputType] = [];
                    groupedImages[category][inputType].push(img);
                });
                setServiceImages(groupedImages);
            } catch (error) {
                console.error("Error fetching service images:", error);
            }
        }
    };

    useEffect(() => {
        fetchServiceImages();
    }, [isEditMode, serviceId, API_BASE_URL, token]);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await fetch(API_BASE_URL + '/api/service-categories/', {
                    headers: {
                        ...(token && { 'Authorization': `Bearer ${token}` }),
                    },
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                setCategories(data);
            } catch (error) {
                setErrorCategories(error.message);
                console.error("Error fetching service categories:", error);
            } finally {
                setLoadingCategories(false);
            }
        };

        fetchCategories();
    }, [API_BASE_URL, token]);

    useEffect(() => {
        const fetchAvailabilityOptions = async () => {
            try {
                const response = await fetch(API_BASE_URL + '/api/service-availability-statuses/', {
                    headers: {
                        ...(token && { 'Authorization': `Bearer ${token}` }),
                    },
                });
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const data = await response.json();
                setAvailabilityOptions(data);
            } catch (error) {
                setErrorAvailability(error.message);
                console.error("Error fetching service availability options:", error);
            } finally {
                setLoadingAvailability(false);
            }
        };

        fetchAvailabilityOptions();
    }, [API_BASE_URL, token]);

    useEffect(() => {
        const fetchTransportOptions = async () => {
            if (selectedCategory === '3' && transportOptions.length === 0) {
                setLoadingTransportOptions(true);
                setErrorTransportOptions(null);
                try {
                    const response = await fetch(API_BASE_URL + '/api/transport-options/', {
                        headers: {
                            ...(token && { 'Authorization': `Bearer ${token}` }),
                        },
                    });
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    const data = await response.json();
                    setTransportOptions(data);
                } catch (error) {
                    setErrorTransportOptions(error.message);
                    console.error("Error fetching transport options:", error);
                } finally {
                    setLoadingTransportOptions(false);
                }
            }
        };

        fetchTransportOptions();
    }, [selectedCategory, API_BASE_URL, token, transportOptions.length]);

    useEffect(() => {
        const fetchFoodOptions = async () => {
            if (selectedCategory === '2' && foodOptions.length === 0) {
                setLoadingFoodOptions(true);
                setErrorFoodOptions(null);
                try {
                    const response = await fetch(API_BASE_URL + '/api/food-options/', {
                        headers: {
                            ...(token && { 'Authorization': `Bearer ${token}` }),
                        },
                    });
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    const data = await response.json();
                    setFoodOptions(data);
                } catch (error) {
                    setErrorFoodOptions(error.message);
                    console.error("Error fetching food options:", error);
                } finally {
                    setLoadingFoodOptions(false);
                }
            }
        };

        fetchFoodOptions();
    }, [selectedCategory, API_BASE_URL, token, foodOptions.length]);

    useEffect(() => {
        const fetchHostingFeatures = async () => {
            if (selectedCategory === '1' && hostingFeatures.length === 0) {
                setLoadingHostingFeatures(true);
                setErrorHostingFeatures(null);
                try {
                    const response = await fetch(API_BASE_URL + '/api/hosting-features/', {
                        headers: {
                            ...(token && { 'Authorization': `Bearer ${token}` }),
                        },
                    });
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    const data = await response.json();
                    setHostingFeatures(data);
                } catch (error) {
                    setErrorHostingFeatures(error.message);
                    console.error("Error fetching hosting features:", error);
                } finally {
                    setLoadingHostingFeatures(false);
                }
            }
        };

        fetchHostingFeatures();
    }, [selectedCategory, API_BASE_URL, token, hostingFeatures.length]);




    const handleTransportOptionChange = (event) => {
        const { value, checked } = event.target;
        setSelectedTransportOptions(prev =>
            checked
                ? [...prev, value]
                : prev.filter(option => option !== value)
        );
    };

    const handleHostingFeatureChange = (event) => {
        const { value, checked } = event.target;
        setSelectedHostingFeatures(prev =>
            checked
                ? [...prev, value]
                : prev.filter(feature => feature !== value)
        );
    };

    const handleFoodOptionChange = (event) => {
        const { value, checked } = event.target;
        setSelectedFoodOptions(prev =>
            checked
                ? [...prev, value]
                : prev.filter(option => option !== value)
        );
    };

    // Combined loading state for the initial data fetches
    const isLoadingInitialData = loadingCategories || loadingAvailability || (isEditMode && isLoadingServiceData);

    // More specific loading states for conditional sections (can be used for more granular spinners if desired)
    // const isLoadingTransportSection = selectedCategory === '3' && loadingTransportOptions;
    // const isLoadingFoodSection = selectedCategory === '2' && loadingFoodOptions;
    // const isLoadingHostingSection = selectedCategory === '1' && loadingHostingFeatures;

    const handleSubmitService = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitMessage({ type: '', text: '' });

        if (!userId) {
            setSubmitMessage({ type: 'danger', text: 'Error: No se pudo identificar al proveedor. Por favor, inicie sesión nuevamente.' });
            setIsSubmitting(false);
            return;
        }
        if (!selectedCategory) {
            setSubmitMessage({ type: 'danger', text: 'Por favor, seleccione una categoría para el servicio.' });
            setIsSubmitting(false);
            return;
        }
        if (!selectedAvailability) {
            setSubmitMessage({ type: 'danger', text: 'Por favor, seleccione la disponibilidad actual del servicio.' });
            setIsSubmitting(false);
            return;
        }

        const payload = {
            supplier_id: parseInt(userId, 10), // Ensure userId is an integer
            name: formData.name,
            description: formData.description,
            category_id: parseInt(selectedCategory, 10),
            availability_status_id: parseInt(selectedAvailability, 10),
            price: formData.price ? parseFloat(formData.price).toFixed(2) : null,
            capacity: formData.capacity ? parseInt(formData.capacity, 10) : null,
            duration: formData.duration,
            start_time: formData.startTime || null,
            end_time: formData.endTime || null,
            weekly_availability: formData.weeklyAvailability,
            additional_conditions: formData.additionalConditions,
            accessibility_info: formData.accessibilityInfo,
            service_origin: formData.serviceOrigin || null,
            service_destination: formData.serviceDestination || null,
            hosting_feature_ids: selectedCategory === '1' ? selectedHostingFeatures.map(id => parseInt(id, 10)) : [],
            food_option_ids: selectedCategory === '2' ? selectedFoodOptions.map(id => parseInt(id, 10)) : [],
            transport_option_ids: selectedCategory === '3' ? selectedTransportOptions.map(id => parseInt(id, 10)) : [],
            // Image handling will be more complex and likely a separate step or different payload structure
        };

        try {
            const url = isEditMode ? `${API_BASE_URL}/services/${serviceId}` : `${API_BASE_URL}/services/`;
            const method = isEditMode ? 'PUT' : 'POST';

            const response = await fetch(`${API_BASE_URL}/api/${url.split('/').pop()}`, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    ...(token && { 'Authorization': `Bearer ${token}` }),
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: `Error del servidor: ${response.status}` }));
                throw new Error(errorData.message || `Error al ${isEditMode ? 'actualizar' : 'crear'} el servicio: ${response.statusText}`);
            }

            const responseData = await response.json(); // Get the created/updated service data

            setSubmitMessage({ type: 'success', text: `¡Servicio ${isEditMode ? 'actualizado' : 'guardado'} con éxito!` });

            if (!isEditMode && responseData && responseData.id) {
                // For new service creation, redirect after a delay.
                // isSubmitting is already true from the start of handleSubmitService, so spinner is active.
                setTimeout(() => {
                    navigate(`/services/detail/${responseData.id}`);
                    setIsSubmitting(false); // Turn off spinner after navigation and delay
                }, 2000);
            } else {
                // For edit mode success, or if new service ID is missing (though this case should ideally be caught by !response.ok).
                setIsSubmitting(false); // Turn off spinner immediately
            }
        } catch (error) {
            console.error("Error submitting service:", error);
            setSubmitMessage({ type: 'danger', text: error.message || `No se pudo ${isEditMode ? 'actualizar' : 'guardar'} el servicio. Intente nuevamente.` });
            setIsSubmitting(false); // Turn off spinner on error
        }
        // Removed the finally block that unconditionally set setIsSubmitting(false)
        // to allow the spinner to persist during the timeout for new service creation.
    };

    useEffect(() => {
        if (submitMessage.text && alertRef.current) {
            alertRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [submitMessage.text]);

    return (
        <DashboardLayout activeMenuItem="Mis Servicios" isScrollable={true}>
            <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3">
                <div className="d-flex align-items-center position-relative"> {/* Added position-relative for potential spinner inside */}
                    <Link to={isEditMode ? `/services/detail/${serviceId}` : '/services'} className="btn btn-back-circle me-3" title="Volver a servicios">
                        <ArrowLeftIcon />
                    </Link>
                    <h1 className="h2 mb-0">{isEditMode ? 'Editar Servicio' : 'Agregar Nuevo Servicio'}</h1>
                </div>
            </div>

            <div className="row mb-4">
                <div className="col-12">
                    <div className="card border-0 text-start">
                        <FormSpinnerOverlay isLoading={isLoadingInitialData || isSubmitting || (isEditMode && isLoadingServiceData)} />
                        <div className="card-body" style={{ opacity: (isLoadingInitialData || isSubmitting) ? 0.7 : 1, transition: 'opacity 0.3s ease-in-out' }}>
                            {/* Container for the alert message.
                                Assign a min-height (e.g., '60px', adjust based on your AlertMessage's typical height)
                                to reserve space. This prevents layout shifts of the form elements below
                                when the alert message appears. The alertRef is on this container.
                            */}
                            <div
                                ref={alertRef}
                                style={{ minHeight: '60px' }} // Adjust this value based on your AlertMessage's actual height
                            >
                                {submitMessage.text && <AlertMessage type={submitMessage.type} message={submitMessage.text} />}
                            </div>
                            <form onSubmit={handleSubmitService}>
                                <div className="row mb-3 gx-md-5">
                                    <div className="col-md-12 mb-3">
                                        <label htmlFor="serviceName" className="form-label h4">Nombre del Servicio</label>
                                        <input type="text" className="form-control" id="serviceName" name="name" value={formData.name} onChange={handleInputChange} placeholder="Ingrese el nombre del servicio" required />
                                    </div>
                                    <div className="col-md-12 mb-3">
                                        <label htmlFor="serviceDescription" className="form-label">Descripción del Servicio</label>
                                        <textarea className="form-control" id="serviceDescription" name="description" value={formData.description} onChange={handleInputChange} rows="4" placeholder="Ingrese una breve descripción del servicio"></textarea>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="serviceCategory" className="form-label">Categoría del Servicio</label>
                                        <select className="form-select" id="serviceCategory" value={selectedCategory} onChange={handleCategoryChange}>
                                            <option value="" disabled>{loadingCategories ? "Cargando categorías..." : "Seleccione una categoría"}</option>
                                            {errorCategories && <option value="" disabled>Error al cargar categorías</option>}
                                            {!loadingCategories && !errorCategories && categories.map(category => (
                                                <option key={category.form_value || category.id} value={category.form_value || category.id}>
                                                    {category.name}
                                                </option>
                                            ))}
                                        </select>
                                        {loadingCategories && <div className="form-text">Cargando...</div>}
                                        {errorCategories && <div className="form-text text-danger">No se pudieron cargar las categorías. Por favor, intente más tarde.</div>}
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="serviceAvailability" className="form-label">Disponibilidad Actual</label>
                                        <select className="form-select" id="serviceAvailability" value={selectedAvailability} onChange={handleAvailabilityChange}>
                                            <option value="" disabled>{loadingAvailability ? "Cargando disponibilidad..." : "Seleccione la disponibilidad"}</option>
                                            {errorAvailability && <option value="" disabled>Error al cargar disponibilidad</option>}
                                            {!loadingAvailability && !errorAvailability && availabilityOptions.map(option => (
                                                <option key={option.form_value || option.id} value={option.form_value || option.id}>
                                                    {option.name}
                                                </option>
                                            ))}
                                        </select>
                                        {loadingAvailability && <div className="form-text">Cargando...</div>}
                                        {errorAvailability && <div className="form-text text-danger">No se pudieron cargar las opciones de disponibilidad.</div>}
                                    </div>

                                    {/* Address Fields - Conditional based on category */}
                                    {['1', '2', '3', '4'].includes(selectedCategory) && (
                                        <div className={selectedCategory === '3' ? 'col-md-6 mb-3' : 'col-md-12 mb-3'}>
                                            <label htmlFor="serviceOrigin" className="form-label">Origen del Servicio</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="serviceOrigin"
                                                name="serviceOrigin"
                                                value={formData.serviceOrigin}
                                                onChange={handleInputChange}
                                                placeholder="Ingrese la ubicación de origen del servicio"
                                            />
                                        </div>
                                    )}

                                    {selectedCategory === '3' && (
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="serviceDestination" className="form-label">Destino del Servicio</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id="serviceDestination"
                                                name="serviceDestination"
                                                value={formData.serviceDestination}
                                                onChange={handleInputChange}
                                                placeholder="Ingrese la ubicación de destino del servicio"
                                            />
                                        </div>
                                    )}
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="servicePrice" className="form-label">Precio del Servicio</label>
                                        <div className="input-group">
                                            <span className="input-group-text">$</span>
                                            <input type="number" className="form-control" id="servicePrice" name="price" value={formData.price} onChange={handleInputChange} placeholder="Ingrese el precio del servicio" step="0.01" />
                                            <span className="input-group-text">.00</span>
                                        </div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="serviceCapacity" className="form-label">Capacidad</label>
                                        <input type="number" className="form-control" id="serviceCapacity" name="capacity" value={formData.capacity} onChange={handleInputChange} placeholder="Ingrese la capacidad del servicio" />
                                    </div>

                                    {/* Conditional Sections Start */}

                                    {selectedCategory === '3' && (
                                        <div id="transporteTiposContainer" className="col-md-12 mb-3 mt-4">
                                            <label className="form-label h4 mb-4">¿Qué tipos de transporte tiene disponible?</label>
                                            {loadingTransportOptions && <div className="form-text">Cargando tipos de transporte...</div>}
                                            {errorTransportOptions && <div className="form-text text-danger">Error al cargar tipos de transporte: {errorTransportOptions}</div>}
                                            {!loadingTransportOptions && !errorTransportOptions && (
                                                <div className="row">
                                                    {transportOptions.map((option) => (
                                                        <div className="col-md-6" key={option.id}>
                                                            <div className="form-check mb-3">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    value={option.id}
                                                                    id={`transport-${option.id}`}
                                                                    name="transporte_tipos[]"
                                                                    checked={selectedTransportOptions.includes(String(option.id))}
                                                                    onChange={handleTransportOptionChange}
                                                                />
                                                                <label className="form-check-label ms-2" htmlFor={`transport-${option.id}`}>{option.name}</label>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {selectedCategory === '2' && (
                                        <div id="alimentacionTiposContainer" className="col-md-12 mb-3 mt-4">
                                            <label className="form-label h4 mb-4">¿Qué tipos de alimentación es?</label>
                                            {loadingFoodOptions && <div className="form-text">Cargando tipos de alimentación...</div>}
                                            {errorFoodOptions && <div className="form-text text-danger">Error al cargar tipos de alimentación: {errorFoodOptions}</div>}
                                            {!loadingFoodOptions && !errorFoodOptions && (
                                                <div className="row">
                                                    {foodOptions.map((option) => (
                                                        // Assuming 3 columns, so col-md-4. Adjust as needed.
                                                        <div className="col-md-4" key={option.id}>
                                                            <div className="form-check mb-3">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    value={option.id}
                                                                    id={`food-${option.id}`}
                                                                    name="alimentacion_tipos[]"
                                                                    checked={selectedFoodOptions.includes(String(option.id))}
                                                                    onChange={handleFoodOptionChange}
                                                                />
                                                                <label className="form-check-label ms-2" htmlFor={`food-${option.id}`}>{option.name}</label>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {selectedCategory === '1' && (
                                        <div id="hospedajeCaracteristicasContainer" className="col-md-12 mb-3 mt-4">
                                            <label className="form-label h4 mb-4">¿Qué características ofrece su servicio de hospedaje?</label>
                                            {loadingHostingFeatures && <div className="form-text">Cargando características de hospedaje...</div>}
                                            {errorHostingFeatures && <div className="form-text text-danger">Error al cargar características: {errorHostingFeatures}</div>}
                                            {!loadingHostingFeatures && !errorHostingFeatures && (
                                                <div className="row">
                                                    {hostingFeatures.map((feature) => (
                                                        // Assuming 3 columns, so col-md-4. Adjust as needed.
                                                        <div className="col-md-4" key={feature.id}>
                                                            <div className="form-check mb-3">
                                                                <input
                                                                    className="form-check-input"
                                                                    type="checkbox"
                                                                    value={feature.id}
                                                                    id={`feature-${feature.id}`}
                                                                    name="hospedaje_caracteristicas[]"
                                                                    checked={selectedHostingFeatures.includes(String(feature.id))}
                                                                    onChange={handleHostingFeatureChange}
                                                                />
                                                                <label className="form-check-label ms-2" htmlFor={`feature-${feature.id}`}>{feature.name}</label>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <h3 className="mt-4 mb-3">Condiciones del Servicio</h3>
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="serviceDuration" className="form-label">Duración del Servicio</label>
                                        <input type="text" className="form-control" id="serviceDuration" name="duration" value={formData.duration} onChange={handleInputChange} placeholder="Ingrese la duración del servicio (ej. 2 horas)" />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label htmlFor="serviceSchedule" className="form-label">Horario de Inicio y Término</label>
                                        <div className="input-group">
                                            <input type="time" className="form-control" id="serviceStartTime" name="startTime" value={formData.startTime} onChange={handleInputChange} placeholder="Hora de inicio" />
                                            <span className="input-group-text">a</span>
                                            <input type="time" className="form-control" id="serviceEndTime" name="endTime" value={formData.endTime} onChange={handleInputChange} placeholder="Hora de término" />
                                        </div>
                                    </div>
                                    <div className="col-md-12 mb-3 mt-3">
                                        <label className="form-label h6">Disponibilidad Semanal</label>
                                        <div className="d-flex justify-content-start day-selector-container flex-wrap">
                                            {['LU', 'MA', 'MI', 'JU', 'VI', 'SA', 'DO'].map(day => (
                                                <div className="day-option" key={`day-${day}`}>
                                                    <input type="checkbox" id={`day-${day.toLowerCase()}`} name="weeklyAvailability" value={day} className="day-checkbox" onChange={handleWeeklyAvailabilityChange} checked={formData.weeklyAvailability.includes(day)} />
                                                    <label htmlFor={`day-${day.toLowerCase()}`} className="day-label">{day}</label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="col-md-12 mb-3 mt-4">
                                        <label htmlFor="serviceConditions" className="form-label h6">Indique condiciones adicionales (opcional)</label>
                                        <textarea className="form-control" id="serviceConditions" name="additionalConditions" value={formData.additionalConditions} onChange={handleInputChange} rows="4" placeholder="Ingrese las condiciones del servicio"></textarea>
                                    </div>

                                    {selectedCategory && (
                                        <div id="galleryMainContainer" className="col-md-12 mb-3 mt-4">
                                            <label className="form-label h4">Galería de Imágenes</label>
                                            <div id="imageUploadSections">
                                                {selectedCategory === '1' && (
                                                    <div id="hospedajeUploads">
                                                        <div className="mb-3">
                                                            <label htmlFor="habitacionPhotos" className="form-label">Fotos de la habitación</label>
                                                            <input
                                                                className="form-control"
                                                                type="file"
                                                                id="habitacionPhotos"
                                                                multiple
                                                                accept="image/*"
                                                                aria-describedby="habitacionPhotosHelp"
                                                                onChange={(e) => handleImageUpload(e, 'hospedaje', 'habitacionPhotos')}
                                                            />
                                                            <div id="habitacionPhotosHelp" className="form-text">Seleccione una o más imágenes de la habitación.</div>
                                                            {renderGallery('hospedaje', 'habitacionPhotos')}
                                                        </div>
                                                        <div className="mb-3">
                                                            <label htmlFor="alojamientoPhotos" className="form-label">Fotos del alojamiento</label>
                                                            <input
                                                                className="form-control"
                                                                type="file"
                                                                id="alojamientoPhotos"
                                                                multiple
                                                                accept="image/*"
                                                                aria-describedby="alojamientoPhotosHelp"
                                                                onChange={(e) => handleImageUpload(e, 'hospedaje', 'alojamientoPhotos')}
                                                            />
                                                            <div id="alojamientoPhotosHelp" className="form-text">Seleccione una o más imágenes del alojamiento (áreas comunes, fachada, etc.).</div>
                                                            {renderGallery('hospedaje', 'alojamientoPhotos')}
                                                        </div>
                                                        <div className="mb-3">
                                                            <label htmlFor="exterioresPhotos" className="form-label">Fotos exteriores</label>
                                                            <input
                                                                className="form-control"
                                                                type="file"
                                                                id="exterioresPhotos"
                                                                multiple
                                                                accept="image/*"
                                                                aria-describedby="exterioresPhotosHelp"
                                                                onChange={(e) => handleImageUpload(e, 'hospedaje', 'exterioresPhotos')}
                                                            />
                                                            <div id="exterioresPhotosHelp" className="form-text">Seleccione una o más imágenes de los exteriores del establecimiento.</div>
                                                            {renderGallery('hospedaje', 'exterioresPhotos')}
                                                        </div>
                                                    </div>
                                                )}
                                                {selectedCategory === '2' && (
                                                    <div id="alimentacionUploads">
                                                        <div className="mb-3">
                                                            <label htmlFor="platoPhotos" className="form-label">Fotos del Plato/Producto</label>
                                                            <input
                                                                className="form-control"
                                                                type="file"
                                                                id="platoPhotos"
                                                                multiple
                                                                accept="image/*"
                                                                aria-describedby="platoPhotosHelp"
                                                                onChange={(e) => handleImageUpload(e, 'alimentacion', 'platoPhotos')}
                                                            />
                                                            <div id="platoPhotosHelp" className="form-text">Seleccione imágenes de los platos o productos ofrecidos.</div>
                                                            {renderGallery('alimentacion', 'platoPhotos')}
                                                        </div>
                                                        <div className="mb-3">
                                                            <label htmlFor="localPhotos" className="form-label">Fotos del Local/Restaurante</label>
                                                            <input
                                                                className="form-control"
                                                                type="file"
                                                                id="localPhotos"
                                                                multiple
                                                                accept="image/*"
                                                                aria-describedby="localPhotosHelp"
                                                                onChange={(e) => handleImageUpload(e, 'alimentacion', 'localPhotos')}
                                                            />
                                                            <div id="localPhotosHelp" className="form-text">Seleccione imágenes del establecimiento (interior, fachada, etc.).</div>
                                                            {renderGallery('alimentacion', 'localPhotos')}
                                                        </div>
                                                        <div className="mb-3">
                                                            <label htmlFor="ambientePhotos" className="form-label">Fotos del Ambiente</label>
                                                            <input
                                                                className="form-control"
                                                                type="file"
                                                                id="ambientePhotos"
                                                                multiple
                                                                accept="image/*"
                                                                aria-describedby="ambientePhotosHelp"
                                                                onChange={(e) => handleImageUpload(e, 'alimentacion', 'ambientePhotos')}
                                                            />
                                                            <div id="ambientePhotosHelp" className="form-text">Seleccione imágenes que muestren el ambiente del lugar.</div>
                                                            {renderGallery('alimentacion', 'ambientePhotos')}
                                                        </div>
                                                    </div>
                                                )}
                                                {selectedCategory === '3' && (
                                                    <div id="transporteUploads">
                                                        <div className="mb-3">
                                                            <label htmlFor="vehiculoInteriorPhotos" className="form-label">Fotos del Vehículo (Interior)</label>
                                                            <input
                                                                className="form-control"
                                                                type="file"
                                                                id="vehiculoInteriorPhotos"
                                                                multiple
                                                                accept="image/*"
                                                                aria-describedby="vehiculoInteriorPhotosHelp"
                                                                onChange={(e) => handleImageUpload(e, 'transporte', 'vehiculoInteriorPhotos')}
                                                            />
                                                            <div id="vehiculoInteriorPhotosHelp" className="form-text">Seleccione imágenes del interior del vehículo.</div>
                                                            {renderGallery('transporte', 'vehiculoInteriorPhotos')}
                                                        </div>
                                                        <div className="mb-3">
                                                            <label htmlFor="vehiculoExteriorPhotos" className="form-label">Fotos del Vehículo (Exterior)</label>
                                                            <input
                                                                className="form-control"
                                                                type="file"
                                                                id="vehiculoExteriorPhotos"
                                                                multiple
                                                                accept="image/*"
                                                                aria-describedby="vehiculoExteriorPhotosHelp"
                                                                onChange={(e) => handleImageUpload(e, 'transporte', 'vehiculoExteriorPhotos')}
                                                            />
                                                            <div id="vehiculoExteriorPhotosHelp" className="form-text">Seleccione imágenes del exterior del vehículo.</div>
                                                            {renderGallery('transporte', 'vehiculoExteriorPhotos')}
                                                        </div>
                                                    </div>
                                                )}
                                                {selectedCategory === '4' && (
                                                    <div id="experienciaUploads">
                                                        <div className="mb-3">
                                                            <label htmlFor="actividadPhotos" className="form-label">Fotos de la Actividad</label>
                                                            <input
                                                                className="form-control"
                                                                type="file"
                                                                id="actividadPhotos"
                                                                multiple
                                                                accept="image/*"
                                                                aria-describedby="actividadPhotosHelp"
                                                                onChange={(e) => handleImageUpload(e, 'experiencia', 'actividadPhotos')}
                                                            />
                                                            <div id="actividadPhotosHelp" className="form-text">Seleccione imágenes representativas de la experiencia o actividad.</div>
                                                            {renderGallery('experiencia', 'actividadPhotos')}
                                                        </div>
                                                        <div className="mb-3">
                                                            <label htmlFor="entornoPhotos" className="form-label">Fotos del Entorno/Lugar</label>
                                                            <input
                                                                className="form-control"
                                                                type="file"
                                                                id="entornoPhotos"
                                                                multiple
                                                                accept="image/*"
                                                                aria-describedby="entornoPhotosHelp"
                                                                onChange={(e) => handleImageUpload(e, 'experiencia', 'entornoPhotos')}
                                                            />
                                                            <div id="entornoPhotosHelp" className="form-text">Seleccione imágenes del lugar donde se realiza la experiencia.</div>
                                                            {renderGallery('experiencia', 'entornoPhotos')}
                                                        </div>
                                                    </div>
                                                )}
                                                {selectedCategory === '5' && (
                                                    <div id="guiasUploads">
                                                        <div className="mb-3">
                                                            <label htmlFor="guiaProfilePhotos" className="form-label">Fotos del Guía/Acompañante</label>
                                                            <input
                                                                className="form-control"
                                                                type="file"
                                                                id="guiaProfilePhotos"
                                                                multiple
                                                                accept="image/*"
                                                                aria-describedby="guiaProfilePhotosHelp"
                                                                onChange={(e) => handleImageUpload(e, 'guias', 'guiaProfilePhotos')}
                                                            />
                                                            <div id="guiaProfilePhotosHelp" className="form-text">Seleccione imágenes del guía o persona que realiza el acompañamiento.</div>
                                                            {renderGallery('guias', 'guiaProfilePhotos')}
                                                        </div>
                                                        <div className="mb-3">
                                                            <label htmlFor="guiaServicioPhotos" className="form-label">Fotos del Servicio/Actividad</label>
                                                            <input
                                                                className="form-control"
                                                                type="file"
                                                                id="guiaServicioPhotos"
                                                                multiple
                                                                accept="image/*"
                                                                aria-describedby="guiaServicioPhotosHelp"
                                                                onChange={(e) => handleImageUpload(e, 'guias', 'guiaServicioPhotos')}
                                                            />
                                                            <div id="guiaServicioPhotosHelp" className="form-text">Seleccione imágenes representativas del servicio de guía o acompañamiento.</div>
                                                            {renderGallery('guias', 'guiaServicioPhotos')}
                                                        </div>
                                                    </div>
                                                )}
                                                {selectedCategory === '6' && (
                                                    <div id="otrosUploads">
                                                        <div className="mb-3">
                                                            <label htmlFor="otrosServicioPhotos" className="form-label">Fotos del Servicio/Producto</label>
                                                            <input
                                                                className="form-control"
                                                                type="file"
                                                                id="otrosServicioPhotos"
                                                                multiple
                                                                accept="image/*"
                                                                aria-describedby="otrosServicioPhotosHelp"
                                                                onChange={(e) => handleImageUpload(e, 'otros', 'otrosServicioPhotos')}
                                                            />
                                                            <div id="otrosServicioPhotosHelp" className="form-text">Seleccione imágenes generales del servicio o producto ofrecido.</div>
                                                            {renderGallery('otros', 'otrosServicioPhotos')}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {['1', '3', '4'].includes(selectedCategory) && (
                                        <div id="accessibilityUploadsContainer" className="col-md-12 mt-4">
                                            <div className="mb-3">
                                                <label htmlFor="accessibilityFeatures" className="form-label h4">Accesibilidad y Adaptaciones</label>
                                                <textarea className="form-control" id="accessibilityFeatures" name="accessibilityInfo" value={formData.accessibilityInfo} onChange={handleInputChange} rows="2" placeholder="Ingrese información sobre accesibilidad y adaptaciones del servicio"></textarea>
                                            </div>
                                            <div className="mb-3">
                                                <label htmlFor="additionalPhotos" className="form-label">
                                                    Fotos Adicionales (Mostrar adecuaciones realizadas)
                                                </label>
                                                <input
                                                    className="form-control"
                                                    type="file"
                                                    id="additionalPhotos"
                                                    multiple
                                                    accept="image/*"
                                                    aria-describedby="additionalPhotosHelp"
                                                    onChange={e => handleImageUpload(e, 'accesibilidad', 'additionalPhotos')}
                                                />
                                                <div id="additionalPhotosHelp" className="form-text">
                                                    Seleccione imágenes que muestren adecuaciones o adaptaciones realizadas para accesibilidad.
                                                </div>
                                                {renderGallery('accesibilidad', 'additionalPhotos')}
                                            </div>
                                        </div>
                                    )}
                                    {/* Conditional Sections End */}

                                    <div className="col-md-12 mt-4">
                                        <button type="submit" className="btn btn-checkin px-4" disabled={isSubmitting || (isEditMode && isLoadingServiceData)}>
                                            {isEditMode ? 'Actualizar Servicio' : 'Guardar Servicio'}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            {/* Modal for lightbox */}
            {modalOpen && modalImg && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: 'rgba(0,0,0,0.7)',
                        zIndex: 2000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                    onClick={handleCloseImageModal}
                >
                    <div
                        style={{
                            position: 'relative',
                            background: '#fff',
                            padding: 16,
                            borderRadius: 8,
                            maxWidth: '90vw',
                            maxHeight: '90vh',
                            boxShadow: '0 2px 16px rgba(0,0,0,0.3)'
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <img
                            src={modalImg.image_url || modalImg.url}
                            alt="Preview"
                            style={{ maxWidth: '80vw', maxHeight: '70vh', display: 'block', margin: '0 auto' }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
                            <button className="btn btn-danger" onClick={() => handleDeleteImage(modalImg)}>
                                Eliminar
                            </button>
                            <button className="btn btn-secondary" onClick={handleCloseImageModal}>
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );

    // Helper function to render image gallery thumbnails
    function renderGallery(category, inputSection) {
        const images = serviceImages[category]?.[inputSection] || [];
        const isUploading = imageUploading[`${category}_${inputSection}`];

        return (
            <div className="image-gallery mt-2 d-flex flex-wrap gap-2" style={{ position: 'relative', minHeight: '110px' }}>
                {isUploading && (
                    <FormSpinnerOverlay isLoading={true} />
                )}
                {images.length > 0 && images.map(img => (
                    <div
                        key={img.id}
                        className="image-thumbnail"
                        style={{ width: '100px', height: '100px', overflow: 'hidden', borderRadius: '4px', border: '1px solid #ddd', cursor: 'pointer' }}
                        onClick={() => handleOpenImageModal(img)}
                    >
                        <img src={img.image_url || img.url} alt="Uploaded" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </div>
                ))}
            </div>
        );
    }

    // Handler for image uploads
    async function handleImageUpload(event, category, inputSection) {
        const files = event.target.files;
        if (!files || files.length === 0) return;
        if (!token || !serviceId) {
            alert('No authentication or service ID available for image upload.');
            return;
        }
        // Set uploading state for this section
        setImageUploading(prev => ({
            ...prev,
            [`${category}_${inputSection}`]: true
        }));

        const formData = new FormData();
        for (let i = 0; i < files.length; i++) {
            formData.append('images', files[i]);
        }
        formData.append('category', category);
        formData.append('inputType', inputSection);

        try {
            const response = await fetch(`${API_BASE_URL}/api/service-images/upload/${serviceId}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.message || 'Failed to upload images');
            }
            // After upload, always re-fetch the images from the backend to ensure state is in sync with DB
            await fetchServiceImages();
            // Clear the input value to allow re-upload of same files if needed
            event.target.value = '';
        } catch (error) {
            console.error('Error uploading images:', error);
            alert(`Error uploading images: ${error.message}`);
        } finally {
            // Unset uploading state for this section
            setImageUploading(prev => ({
                ...prev,
                [`${category}_${inputSection}`]: false
            }));
        }
    }

    // Modal state for lightbox (top-level, outside renderGallery)
    // Move these to the top of the component, not after renderGallery, to follow React rules of hooks.
    // Place these right after the other useState hooks at the top of the component.

}

export default ServiceFormSupplierScreen;